import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, Edit, Clock } from "lucide-react";

import {
  getAllEmployeesForSAQueryOptions,
  getEmployeeCheckinsByDateQueryOptions,
} from "@/config/queries/superadmin-query-options";
import { updateCheckinTime } from "@/config/mutation/super-admin";
import { Button } from "@/components/ui/button/button";
import { Calendar } from "@/components/ui/calendar/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog/dialog";
import { Badge } from "@/components/ui/badge/badge";
import { NetworkBadge } from "@/components/network-badge";
import { DateTimePicker } from "@/components/ui/datetime-picker";
import { Textarea } from "@/components/ui/textarea/textarea";
import { cn } from "@/lib/utils";

const CheckinManagementPage = () => {
  const key = Cookies.get("key") as string;
  const queryClient = useQueryClient();

  // State for filters
  const [selectedEmployee, setSelectedEmployee] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    new Date()
  );

  // State for edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingCheckin, setEditingCheckin] = useState<{
    id: string;
    currentTime: Date;
    mode: string;
    isLate: boolean;
  } | null>(null);
  const [newTime, setNewTime] = useState<Date | undefined>(undefined);
  const [newIsLate, setNewIsLate] = useState(false);
  // Every correction says why; the API keeps it with the change.
  const [reason, setReason] = useState("");

  // Fetch employees
  const { data: employeesData, isPending: isEmployeesPending } = useQuery(
    getAllEmployeesForSAQueryOptions(key)
  );

  // Fetch check-ins for selected employee and date
  const { data: checkinsData, isPending: isCheckinsPending } = useQuery(
    getEmployeeCheckinsByDateQueryOptions(
      key,
      selectedEmployee,
      selectedDate ? selectedDate.toISOString() : ""
    )
  );

  // Mutation for updating check-in time
  const updateMutation = useMutation({
    mutationFn: updateCheckinTime(key),
    onSuccess: () => {
      toast.success("Check-in time updated successfully");
      queryClient.invalidateQueries({ queryKey: ["sa-checkins-by-date"] });
      setEditModalOpen(false);
      setEditingCheckin(null);
      setNewTime(undefined);
      setReason("");
    },
    onError: (error: Error & { response?: { data?: { error?: string } } }) => {
      toast.error(error.response?.data?.error || error.message || "Failed to update check-in time");
    },
  });

  // Prepare table data
  const checkinsTableData = useMemo(() => {
    if (isCheckinsPending || !checkinsData?.checkins) return [];

    return checkinsData.checkins.map((checkin) => ({
      id: checkin._id,
      mode: checkin.mode,
      time: new Date(checkin.createdAt),
      isLate: checkin.isLate,
      notes: checkin.notes,
      workDoneToday: checkin.workDoneToday,
      device: checkin.device,
      browser: checkin.browser,
      ip: checkin.ip,
      officeNetwork: checkin.officeNetwork,
      serverIp: checkin.serverIp,
    }));
  }, [checkinsData, isCheckinsPending]);

  // Handle edit click
  const handleEditClick = (checkin: {
    id: string;
    time: Date;
    mode: string;
    isLate: boolean;
  }) => {
    setEditingCheckin({
      id: checkin.id,
      currentTime: checkin.time,
      mode: checkin.mode,
      isLate: checkin.isLate,
    });
    setNewTime(checkin.time);
    setNewIsLate(checkin.isLate);
    setReason("");
    setEditModalOpen(true);
  };

  // Handle save
  const handleSave = () => {
    // A reason is optional (user, 2026-10-19): the change is kept in the history either way.
    if (newTime && editingCheckin) {
      updateMutation.mutate({
        checkinId: editingCheckin.id,
        newTime: newTime.toISOString(),
        isLate: newIsLate,
        reason: reason.trim(),
      });
    }
  };

  return (
    <div className="p-5 h-[calc(100%-56px)]">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-2xl font-medium">Check-in/Check-out Management</h3>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-5 flex-wrap">
        {/* Date Picker */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "w-[240px] justify-start text-left font-normal",
                !selectedDate && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {selectedDate ? (
                format(selectedDate, "PPP")
              ) : (
                <span>Select date</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
            />
          </PopoverContent>
        </Popover>

        {/* Employee Selector */}
        <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
          <SelectTrigger className="w-[300px]">
            <SelectValue placeholder="Select an employee" />
          </SelectTrigger>
          <SelectContent>
            {isEmployeesPending ? (
              <SelectItem value="loading" disabled>
                Loading...
              </SelectItem>
            ) : (
              employeesData?.employees?.map((emp) => (
                <SelectItem key={emp._id} value={emp._id}>
                  {emp.name} ({emp.email})
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>

      {/* Content */}
      {selectedEmployee && selectedDate ? (
        isCheckinsPending ? (
          <div className="flex items-center justify-center py-10">
            <div className="text-muted-foreground">Loading check-ins...</div>
          </div>
        ) : checkinsTableData.length > 0 ? (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Mode</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Late</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Work Done</TableHead>
                  <TableHead>Device Info</TableHead>
                  <TableHead>Network</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {checkinsTableData.map((checkin) => (
                  <TableRow key={checkin.id}>
                    <TableCell>
                      <Badge
                        variant={
                          checkin.mode === "IN" ? "default" : "secondary"
                        }
                      >
                        {checkin.mode === "IN" ? "Check In" : "Check Out"}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono">
                      {format(checkin.time, "PPpp")}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={checkin.isLate ? "destructive" : "outline"}
                      >
                        {checkin.isLate ? "Yes" : "No"}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-[150px] truncate">
                      {checkin.notes || "-"}
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {checkin.workDoneToday || "-"}
                    </TableCell>
                    <TableCell className="max-w-[150px] truncate text-xs text-muted-foreground">
                      {checkin.device || checkin.browser || "-"}
                    </TableCell>
                    <TableCell>
                      <NetworkBadge
                        officeNetwork={checkin.officeNetwork}
                        serverIp={checkin.serverIp}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditClick(checkin)}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            No check-ins found for the selected employee and date.
          </div>
        )
      ) : (
        <div className="flex items-center justify-center py-10 text-muted-foreground">
          Please select an employee and date to view check-ins.
        </div>
      )}

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Edit {editingCheckin?.mode === "IN" ? "Check-in" : "Check-out"}{" "}
              Time
            </DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">
                Current Time
              </label>
              <div className="text-muted-foreground font-mono">
                {editingCheckin?.currentTime
                  ? format(editingCheckin.currentTime, "PPpp")
                  : "-"}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">New Time</label>
              <DateTimePicker
                value={newTime}
                onChange={setNewTime}
                hourCycle={24}
                granularity="minute"
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Mark as Late</label>
              <button
                type="button"
                role="switch"
                aria-checked={newIsLate}
                onClick={() => setNewIsLate(!newIsLate)}
                className={cn(
                  "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors",
                  newIsLate ? "bg-destructive" : "bg-input"
                )}
              >
                <span
                  className={cn(
                    "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow-lg ring-0 transition-transform",
                    newIsLate ? "translate-x-5" : "translate-x-0"
                  )}
                />
              </button>
            </div>

            <div>
              <label htmlFor="checkin-edit-reason" className="text-sm font-medium mb-2 block">
                Reason (optional)
              </label>
              <Textarea
                id="checkin-edit-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={300}
                placeholder="Why is this time being changed?"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditModalOpen(false);
                setEditingCheckin(null);
                setNewTime(undefined);
                setReason("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={updateMutation.isPending || !newTime}
            >
              {updateMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CheckinManagementPage;
